import { useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { milesToMeters } from '@goodfynd/utils';

import config from '../../config';
import { useVendorApi } from '../../hooks/api';
import useGeoSearch from '../../hooks/useGeoSearch';
import type { VendorScheduleSearchResponse } from '../../services/api/types';
import { useMobileSize } from '../../utils/base';
import { getPreOrderDayOptions } from '../../utils/vendor-util';

import type { UseScheduleEmbedOptions } from './types';

const isTruthy = (value: boolean | string | undefined): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return false;
};

const toNumber = (
  value: number | string | undefined,
  fallback: number
): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function useScheduleEmbed({
  dayPickerLabel = 'Next 5 days',
  hideDayPicker = 'false',
  hideLocationSearch = 'false',
  isReady,
  latitude,
  longitude,
  organizationId,
  radius = '50',
  rows = '100',
  selectZoom = '19',
  vendorId,
}: UseScheduleEmbedOptions = {}) {
  const api = useVendorApi();

  const dayOptions = getPreOrderDayOptions({
    label: dayPickerLabel as string,
    value: '',
  });

  const [selectedDate, setSelectedDate] = useState<string>(
    dayOptions[0]?.value
  );
  const [total, setTotal] = useState(0);

  const showDayPicker = isTruthy(hideDayPicker) !== true;
  const showLocationSearch = isTruthy(hideLocationSearch) !== true;
  const hasFilters = showDayPicker || showLocationSearch;

  const organizationIdValue = organizationId?.toString() || '';
  const vendorIdValue = vendorId?.toString() || '';
  const radiusMiles = toNumber(radius, 50);
  const rowsPerPage = toNumber(rows, 100);

  const { busy, location, ...geoSearch } = useGeoSearch({
    store: true,
  });

  const isMobile = useMobileSize();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery<VendorScheduleSearchResponse, unknown>(
    [
      config.queryKeys.openVendors,
      `${location?.latitude},${location?.longitude}`,
      organizationIdValue,
      selectedDate,
      vendorIdValue,
    ],
    ({ pageParam = 1 }) =>
      api.getOpenVendors({
        coordinates: location
          ? `${location.latitude},${location.longitude}`
          : '',
        date: selectedDate,
        page: pageParam,
        radius: Math.ceil(milesToMeters(radiusMiles)),
        rows: rowsPerPage,
        vendorIds: vendorIdValue,
        vendorOrganizationId: organizationIdValue,
      }),
    {
      enabled: isReady && !!location,
      getNextPageParam: (lastPage) =>
        lastPage.hasMore ? lastPage.page + 1 : undefined,
      onSuccess: (data) => {
        const pageData = data.pages.at(-1);
        if (!pageData) {
          return;
        }

        setTotal(pageData?.total);
      },
    }
  );

  const center = useMemo(() => {
    return {
      latitude: toNumber(latitude, Number(location?.latitude) || 0),
      longitude: toNumber(longitude, Number(location?.longitude) || 0),
    };
  }, [latitude, location?.latitude, location?.longitude, longitude]);

  const results = useMemo(
    () => data?.pages?.flatMap((page) => page.results) || [],
    [data]
  );

  return {
    busy,
    center,
    data,
    dayOptions,
    fetchNextPage,
    geoSearch,
    hasFilters,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isMobile,
    isReady,
    location,
    results,
    selectZoom,
    selectedDate,
    setSelectedDate,
    showDayPicker,
    showLocationSearch,
    total,
  };
}
